import dayjs from 'dayjs';

import type { ISeriesSegment, ITrendingChartSeries } from './types';

export const DEFAULT_TRENDING_CHART_PALETTE = [
  'var(--color-error)',
  'var(--ant-gold-6)',
  'var(--ant-green-6)',
  'var(--ant-blue-6)',
];

export const sanitizeSeries = (
  series: Array<ITrendingChartSeries | null | undefined>,
): ITrendingChartSeries[] =>
  series
    .filter((item): item is ITrendingChartSeries => Boolean(item))
    .map((item, index) => ({
      ...item,
      color:
        item.color ||
        DEFAULT_TRENDING_CHART_PALETTE[
          index % DEFAULT_TRENDING_CHART_PALETTE.length
        ],
    }));

export const collectValidValues = (series: ITrendingChartSeries[]): number[] =>
  series.flatMap((item) =>
    item.values.filter(
      (value): value is number =>
        typeof value === 'number' && Number.isFinite(value),
    ),
  );

export const hasChartData = (series: ITrendingChartSeries[]) =>
  series.some((item) =>
    item.values.some(
      (value) => typeof value === 'number' && Number.isFinite(value),
    ),
  );

export const buildSeriesSegments = (values: ITrendingChartSeries['values']) => {
  const segments: ISeriesSegment[] = [];
  let current: ISeriesSegment = [];

  values.forEach((value, index) => {
    if (typeof value === 'number' && Number.isFinite(value)) {
      current.push({ x: index, y: value });
      return;
    }

    if (current.length > 0) {
      segments.push(current);
      current = [];
    }
  });

  if (current.length > 0) {
    segments.push(current);
  }

  return segments;
};

export type TTrendingLabelRange = {
  start: dayjs.Dayjs;
  end: dayjs.Dayjs;
} | null;

const LABEL_DELIMITER = ' - ';

export const parseTrendingLabelRange = (
  label?: string,
): TTrendingLabelRange => {
  if (!label) {
    return null;
  }

  const normalized = label.trim();
  if (!normalized) {
    return null;
  }

  if (normalized.includes(LABEL_DELIMITER)) {
    const [startRaw, endRaw] = normalized.split(LABEL_DELIMITER);
    const start = dayjs(startRaw?.trim());
    const end = dayjs(endRaw?.trim());

    if (start.isValid() && end.isValid()) {
      return { start, end };
    }
  }

  const parsed = dayjs(normalized);
  return parsed.isValid() ? { start: parsed, end: parsed } : null;
};

export const formatTrendingLegendLabel = (
  range: TTrendingLabelRange,
  fallback = '',
): string => {
  if (!range) {
    return fallback;
  }

  const { start, end } = range;

  if (start.isSame(end, 'day')) {
    return start.format('MMM DD');
  }

  const sameYear = start.year() === end.year();
  const sameMonth = sameYear && start.month() === end.month();

  if (sameMonth) {
    return `${start.format('MMM DD')}-${end.format('DD')}`;
  }
  return `${start.format('MMM DD')}-${end.format('MMM DD')}`;
};

export const formatTrendingTooltipLabel = (
  range: TTrendingLabelRange,
  fallback = '',
): string => {
  if (!range) {
    return fallback;
  }

  const { start, end } = range;

  if (start.isSame(end, 'day')) {
    return start.format('MMM DD, YYYY');
  }

  const sameYear = start.year() === end.year();
  const sameMonth = sameYear && start.month() === end.month();

  if (sameYear) {
    if (sameMonth) {
      return `${start.format('MMM DD')}-${end.format('DD')}, ${start.format(
        'YYYY',
      )}`;
    }
    return `${start.format('MMM DD')} - ${end.format('MMM DD')}, ${start.format(
      'YYYY',
    )}`;
  }

  return `${start.format('MMM DD, YYYY')} - ${end.format('MMM DD, YYYY')}`;
};

export const hasYearChanged = (ranges: string[]): boolean => {
  if (ranges.length === 0) return false;

  const firstYear = ranges[0].slice(0, 4);
  const lastRange = ranges.at(-1)!;
  const dateParts = lastRange.split(' - ');
  const lastDate = dateParts.length > 1 ? dateParts[1] : lastRange;
  const lastYear = lastDate.slice(0, 4);
  return firstYear !== lastYear;
};

export const formatCrossYearAxisLabel = (
  ranges: TTrendingLabelRange[],
): string | null => {
  const firstRange = ranges.find(
    (range): range is NonNullable<TTrendingLabelRange> => Boolean(range),
  );
  const lastRange = [...ranges]
    .reverse()
    .find((range): range is NonNullable<TTrendingLabelRange> => Boolean(range));

  if (!firstRange || !lastRange) {
    return null;
  }

  return `${firstRange.start.format('DD MMM YYYY')} - ${lastRange.end.format(
    'DD MMM YYYY',
  )}`;
};
